import React, { createContext, JSX, ReactNode, useContext, useMemo } from 'react';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { DefaultApi, Configuration } from './../generated/axios';
import type { InternalAxiosRequestConfig } from 'axios';

const axiosInstance: AxiosInstance = axios.create();

function getToken(): string | undefined {
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('oidc.user:')) {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          return parsed.access_token;
        } catch (e) {
          console.error('Errore parsing token OIDC:', e);
        }
      }
    }
  }
  return undefined;
}

// Interceptor per aggiungere Authorization header con Bearer token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.headers) {
      config.headers = new axios.AxiosHeaders();
    }
    const token = getToken();
    if (token) {
      (config.headers as any)['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor per gestire errori globali
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        // 🔄 Prova a rigenerare access_token usando refresh_token
        const refreshToken = sessionStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            'https://gksd-product-matching-app-login.auth.eu-south-1.amazoncognito.com/oauth2/token',
            new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: '6uqpa8c2rpued6kgt49js036d8',
              refresh_token: refreshToken,
            }),
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
            },
          );

          const newToken = response.data.access_token;
          sessionStorage.setItem('access_token', newToken);
          error.config.headers['Authorization'] = `Bearer ${newToken}`;
          return axiosInstance.request(error.config);
        }
      } catch (refreshError) {
        // Se anche il refresh fallisce → logout
        sessionStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

interface ApiContextType {
  defaultApi: DefaultApi;
  //aggiungere altre API qui
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export function ApiContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const config = useMemo(() => new Configuration({ accessToken: getToken() }), []);

  const apiValue = useMemo<ApiContextType>(
    () => ({
      defaultApi: new DefaultApi(config, undefined, axiosInstance),
    }),
    [config],
  );

  return <ApiContext.Provider value={apiValue}>{children}</ApiContext.Provider>;
}

// Hook per usare il contesto Api
export function useApi(): ApiContextType {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiContextProvider');
  }
  return context;
}
